import React from "react";
import { Box, Paper, Typography, Button, Chip, Alert, Switch } from "@mui/material";
import globals from "../../Globals";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { profile, canEdit, ConfirmAction, COSMETIC_KINDS } from "./community";

// Profiles → Cosmetics (service `profile`, Docs/Social.md §14.1 / §14.8 / §14.9): the title's catalogue `cosmetics_<title>` (title-wide,
// every branch) - `profile catalogue` - with `profile enable <id> on|off` (a disabled one stays owned and visible; Set refuses it),
// `profile import` with the repo's content file as the body (TowersBackend/Content/<title>/cosmetics.json: rows upserted, rows not in
// the file kept and listed; one bad row refuses the whole file), and the one-off `profile migrate-private [dry-run]` (the title's
// old privacy flag -> profile.private). `#profile` views; `profile` edits, imports and migrates.
const RARITY = ["common", "uncommon", "rare", "epic", "legendary"];

export default class ProfilesCatalogueTab extends React.Component {
    state = { data: null, kind: "all", loading: false, busy: false, action: null, notice: null, migration: null };
    fileInput = React.createRef();

    componentDidMount() { this.load(); }

    load = () => {
        this.setState({ loading: true });
        profile("Loading the catalogue", ["catalogue"], { silent: true })
            .then(r => this.setState({ data: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState({ data: null, notice: { severity: "warning", text: e?.message ?? "failed" } }))
            .finally(() => this.setState({ loading: false }));
    }

    act = (parts, description, options, apply) => {
        this.setState({ busy: true });
        return profile(description, parts, { successMessage: "Answered", ...options })
            .then(r => { this.setState({ action: null }); apply(r); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    toggle = (c) => this.setState({ action: {
        title: `${c.enabled ? "Withdraw" : "Offer"} ${c.id}?`, confirmText: c.enabled ? "Disable" : "Enable",
        text: c.enabled ? "Disabled: whoever owns it keeps it (shown, not selectable: Set refuses it); grants still land." : "Enabled: owners may select it again.",
        run: () => this.act(["enable", c.id, c.enabled ? "off" : "on"], c.enabled ? "Disabling the cosmetic" : "Enabling the cosmetic", {}, r => {
            const saved = r?.saved;
            if (saved) this.setState(s => ({ data: { ...s.data, cosmetics: (s.data?.cosmetics ?? []).map(x => x.id === saved.id ? saved : x) } }));
            else this.load();
        }),
    }});

    // The file picked from the repo: parsed here, posted whole as the body (the handler takes the body when it carries `cosmetics`)
    pickFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            let body;
            try { body = JSON.parse(String(reader.result)); } catch (err) { this.setState({ notice: { severity: "error", text: `${file.name} is not JSON: ${err.message}` } }); return; }
            const rows = Array.isArray(body?.cosmetics) ? body.cosmetics : null;
            if (!rows || rows.length === 0) { this.setState({ notice: { severity: "error", text: `${file.name} has no "cosmetics" rows.` } }); return; }
            const perKind = COSMETIC_KINDS.map(k => `${rows.filter(r => r?.kind === k).length} ${k}`).join(", ");
            this.setState({ action: {
                title: `Import ${file.name}?`, confirmText: "Import",
                text: `${rows.length} rows (${perKind}) of ${body.title || "an unnamed title"} into ${globals.selectedTitle}'s catalogue (every branch). New ids are added, changed rows replaced, rows not in the file kept. One invalid row refuses the whole file.`,
                run: () => this.act(["import"], "Importing the catalogue", { payload: body }, r => {
                    if (r?.refused) { this.setState({ notice: { severity: "error", text: `Import refused: ${[].concat(r.refused).join(" ")}` } }); return; }
                    const kept = r?.notInFile ?? [];
                    this.setState({ notice: { severity: "success", text: `${r?.rows ?? "?"} rows from ${file.name}: ${r?.added ?? 0} added, ${r?.changed ?? 0} changed, ${r?.unchanged ?? 0} unchanged${kept.length ? `; ${kept.length} not in the file (kept): ${kept.join(", ")}` : ""}.` } });
                    this.load();
                }),
            }});
        };
        reader.onerror = () => this.setState({ notice: { severity: "error", text: `Could not read ${file.name}.` } });
        reader.readAsText(file);
    }

    migrate = (dry) => {
        const run = () => this.act(["migrate-private", ...(dry ? ["dry-run"] : [])], dry ? "Counting the rows to migrate" : "Migrating the private flags", {}, r =>
            this.setState({ migration: { ...r, ranAt: new Date() } }));
        if (dry) { run(); return; }
        this.setState({ action: {
            title: "Migrate the private flags?", confirmText: "Run",
            text: "Every row with the title's old privacy flag true and no profile.private gets profile.private = true (+ its cosmetics version). Idempotent: a second run finds nothing.",
            run,
        }});
    }

    render() {
        const { data, kind, loading, busy, action, notice, migration } = this.state;
        const edit = canEdit("profile");
        const rows = (data?.cosmetics ?? []).filter(c => kind === "all" || c.kind === kind);
        const mono = { fontFamily: "monospace", wordBreak: "break-all" };
        const columns = [
            { id: "id", label: "Id", width: "17%", render: c => <span style={mono}>{c.id}{c.id === data?.defaultPicture ? <Chip size="small" label="default" sx={{ ml: 1 }} /> : null}</span> },
            { id: "kind", label: "Kind", width: "8%" },
            { id: "art", label: "Art", width: "17%", render: c => c.art ? <span style={mono}>{c.art}</span> : <Typography variant="body2" color="text.secondary">(none)</Typography> },
            { id: "name", label: "Name tag", width: "20%", render: c => <span style={{ wordBreak: "break-all" }}>{c.name}</span> },
            { id: "rarity", label: "Rarity", width: "9%", render: c => `${c.rarity} ${RARITY[c.rarity] ?? ""}` },
            { id: "source", label: "Source tag", width: "16%", render: c => <span style={{ wordBreak: "break-all" }}>{c.source || "—"}</span> },
            { id: "order", label: "Order", width: "5%", align: "right" },
            { id: "enabled", label: "Enabled", width: "8%", align: "center", render: c => edit
                ? <Switch size="small" checked={!!c.enabled} disabled={busy} onChange={() => this.toggle(c)} />
                : (c.enabled ? "yes" : <Chip size="small" color="warning" label="no" />) },
        ];
        const kinds = [{ value: "all", label: `All (${data?.count ?? 0})` }, ...COSMETIC_KINDS.map(k => ({ value: k, label: `${k} (${data?.perKind?.[k] ?? 0})` }))];
        return (
            <PageContainer>
                <SectionHeader title="Cosmetics">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · the catalogue every branch reads · default picture {data?.defaultPicture || "—"}</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Kind" size="small" minWidth={160} value={kind} options={kinds} onChange={v => this.setState({ kind: v })} />
                    <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                    {edit && (
                        <>
                            <Button variant="contained" onClick={() => this.fileInput.current?.click()} disabled={busy}>Import from repo JSON</Button>
                            <input ref={this.fileInput} type="file" accept=".json,application/json" style={{ display: "none" }} onChange={this.pickFile} />
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>TowersBackend/Content/{globals.selectedTitle}/cosmetics.json</Typography>
                        </>
                    )}
                </SelectorBar>
                {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => this.setState({ notice: null })}>{notice.text}</Alert>}
                {rows.length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={columns} rows={rows} getRowKey={c => c.id} />
                    : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{loading || !data ? "Loading…" : "No cosmetic of this kind: import the content file."}</Typography></Paper>}

                {edit && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Privacy migration (one-off, after the deploy)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            The title's old privacy flag becomes profile.private on every row that has it true and no profile.private yet. Dry run first: it only counts.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <Button variant="outlined" onClick={() => this.migrate(true)} disabled={busy}>Dry run</Button>
                            <Button variant="contained" color="warning" onClick={() => this.migrate(false)} disabled={busy}>Run</Button>
                            {migration && (
                                <Typography variant="body2">
                                    {migration.done
                                        ? migration.done
                                        : <>{migration.dryRun ? "Dry run" : "Run"}: <b>{migration.matched}</b> row(s) with {migration.from} and no {migration.to}, <b>{migration.written}</b> written</>}
                                    <Typography component="span" variant="caption" color="text.secondary"> · {migration.ranAt.toLocaleString()}</Typography>
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
